import { useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { PencilIcon } from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Button } from '@ems/ui/components/button'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { AddMemberForm, MembersTable } from '#admin/features/exhibitor/company-members.tsx'
import { CompanyProfileDialog } from '#admin/features/exhibitor/company-profile-dialog.tsx'
import { CompanyProfileView } from '#admin/features/exhibitor/company-profile-view.tsx'
import { ServiceOrdersSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { teamQuery } from '#admin/features/exhibitor/queries.ts'

/**
 * `/exhibition/companies/$companyId` — the profile and the members of one exhibitor company.
 *
 * The profile shows as values to read. "Edit" opens the dialog that "New company" also uses.
 *
 * A member can do all that the applicant can do in the portal. The operator adds a member by
 * email, and there is no accept step.
 */
export const Route = createFileRoute('/_authenticated/exhibition/companies/$companyId')({
  // ponytail: the service orders skeleton has the same shape (a header, a panel and a table).
  pendingComponent: ServiceOrdersSkeleton,
  staticData: { crumb: useCompanyCrumb },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(teamQuery(params.companyId))
    } catch (error) {
      // A company that does not exist is "not here". A module missing from the plan is also a
      // 404, and the ErrorView explains that one.
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: CompanyPage,
})

const CARD = 'bg-card flex flex-col gap-6 rounded-lg border border-(--border) p-4 md:p-6'

/** "Companies / Nusantara Energi". */
function useCompanyCrumb(): string {
  const { companyId } = Route.useParams()
  const { data } = useQuery(teamQuery(companyId))
  return data?.company.name ?? 'Company'
}

function CompanyPage() {
  const { companyId } = Route.useParams()
  const { data } = useSuspenseQuery(teamQuery(companyId))
  const { data: session } = useSuspenseQuery(sessionQuery())
  // A view-only role sees the members and no verbs. The server refuses them anyway.
  const canManage = can(session, 'exhibitor.manage')
  const [editing, setEditing] = useState(false)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={data.company.name}
        description="The profile and the members of this company. Each member can do all that the applicant can do in the portal."
      />

      {canManage ? null : <ReadOnlyNotice area="this company" />}

      <section aria-labelledby="company-profile" className={CARD}>
        <div className="flex items-center justify-between gap-4">
          <h2 id="company-profile" className="text-base font-semibold">
            Profile
          </h2>
          {canManage ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <PencilIcon />
              Edit
            </Button>
          ) : null}
        </div>
        <CompanyProfileView company={data.company} />
      </section>

      {editing ? (
        <CompanyProfileDialog company={data.company} onClose={() => setEditing(false)} />
      ) : null}

      {canManage ? <AddMemberForm companyId={companyId} /> : null}

      <MembersTable companyId={companyId} members={data.members} canManage={canManage} />
    </div>
  )
}
